import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import SubsWall, { STYLE_PALETTES, type SubTile, type Theme, type TileStyle } from "../components/SubsWall";
import "./LandingV5.css";

type SlideKey = Theme | "custom";
const WALL_THEMES: Array<{ key: SlideKey; label: string; tagline: string }> = [
  { key: "neon",       label: "Neon",       tagline: "Streamer billboards" },
  { key: "chalkboard", label: "Chalkboard", tagline: "Classroom · chalk handwriting" },
  { key: "stickies",   label: "Stickies",   tagline: "Cork board · sticky notes" },
  { key: "custom",     label: "Custom",     tagline: "Upload your own background" },
];

// Inline SVG that demos the "upload your own background" affordance.
// A subtle galaxy pattern in coral + baby blue, encoded so the landing
// works without depending on any external image host.
const CUSTOM_DEMO_BG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 600">
      <defs>
        <radialGradient id="g1" cx="30%" cy="40%" r="50%">
          <stop offset="0%" stop-color="#ff7ab0" stop-opacity="0.9"/>
          <stop offset="100%" stop-color="#5fa8ff" stop-opacity="0"/>
        </radialGradient>
        <radialGradient id="g2" cx="75%" cy="65%" r="40%">
          <stop offset="0%" stop-color="#5fa8ff" stop-opacity="0.85"/>
          <stop offset="100%" stop-color="#1a1144" stop-opacity="0"/>
        </radialGradient>
      </defs>
      <rect width="800" height="600" fill="#1a1144"/>
      <rect width="800" height="600" fill="url(#g1)"/>
      <rect width="800" height="600" fill="url(#g2)"/>
      <g fill="#ffffff">
        <circle cx="120" cy="80"  r="1.5"/>
        <circle cx="540" cy="120" r="2"/>
        <circle cx="680" cy="80"  r="1"/>
        <circle cx="220" cy="280" r="1.5"/>
        <circle cx="380" cy="180" r="1"/>
        <circle cx="700" cy="380" r="1.5"/>
        <circle cx="80"  cy="420" r="2"/>
        <circle cx="600" cy="500" r="1"/>
        <circle cx="320" cy="540" r="1.5"/>
      </g>
    </svg>`
  );
const SLIDE_MS = 5000;

function makeHeroTiles(): SubTile[] {
  // Five tiles — enough variety to read as a real wall, sparse enough to
  // never overlap on the narrow hero preview canvas.
  const seed: Array<Partial<SubTile> & { name: string; message: string; monthlyCents: number; style: TileStyle }> = [
    { name: "Sarah K.",     message: "first one in",          monthlyCents: 500,   style: "spray" },
    { name: "Marcus T.",    message: "$5/mo forever",         monthlyCents: 500,   style: "chalk" },
    { name: "lofi.ghost",   message: "your VOD on loop",      monthlyCents: 1000,  style: "neon", blink: true },
    { name: "Maya Chen",    message: "since the API days",    monthlyCents: 2500,  style: "carved", badge: "shield" },
    { name: "Helena Voss",  message: "day-one founder",       monthlyCents: 10000, style: "neon", blink: true, badge: "star", founderNumber: 1 },
  ];
  // 3 × 3 grid distribution with mild jitter so the hero wall preview doesn't
  // pile tiles on top of each other.
  return seed.map((s, i) => {
    // Hand-tuned non-overlapping positions for the 5 hero tiles.
    // Diagonal stagger reads as "a living wall" without crowding.
    const positions: Array<[number, number]> = [
      [25, 22], // KAIROS — top-left
      [70, 24], // ZINC — top-right
      [22, 55], // lo-fi-ghost — middle-left
      [62, 56], // VEXLINE — middle-right
      [44, 82], // AURORA — bottom-center, larger
    ];
    const [xPct, yPct] = positions[i] ?? [50, 50];
    return {
      id: `h${i}`,
      name: s.name,
      message: s.message,
      style: s.style,
      color: STYLE_PALETTES[s.style][(i + 1) % 8],
      xPct, yPct,
      monthlyCents: s.monthlyCents,
      anniversaryMonths: 0,
      founderNumber: s.founderNumber ?? null,
      boostUntil: null,
      fadeStartedAt: null,
      badge: s.badge ?? null,
      blink: s.blink ?? false,
    };
  });
}

// Mock creator cards — what a "trending creators" grid looks like
const EXAMPLE_CREATORS = [
  { handle: "frame_rate",     name: "Frame Rate",       category: "Film YouTube",      supporters: 412,  emoji: "🎬" },
  { handle: "the_essayist",   name: "The Essayist",     category: "Writing",           supporters: 287,  emoji: "✍️" },
  { handle: "open_pasta",     name: "Open Pasta",       category: "Cooking",           supporters: 1024, emoji: "🍝" },
  { handle: "deeplog",        name: "DeepLog",          category: "Tech podcast",      supporters: 638,  emoji: "🎙️" },
  { handle: "studio_dao",     name: "Studio DAO",       category: "Art collective",    supporters: 192,  emoji: "🎨" },
  { handle: "low_orbit",      name: "Low Orbit",        category: "Astro photography", supporters: 503,  emoji: "🌌" },
];

const TESTIMONIALS = [
  {
    name: "Helena Voss",
    role: "Tech essayist · 24k subs",
    quote: "Patreon was a part-time job. SubsWall is one wall I never touch — and revenue's up 40%.",
  },
  {
    name: "Marcus T.",
    role: "Film critic · 1.2M views",
    quote: "My fans wanted to support me. They didn't want another paywalled blog. The wall solves both.",
  },
  {
    name: "Camille Joubert",
    role: "Newsletter writer",
    quote: "I cancelled Patreon the day I set up my wall. Same MRR, zero admin.",
  },
];

export default function LandingV5(): JSX.Element {
  const [tiles] = useState<SubTile[]>(makeHeroTiles);
  const [themeIdx, setThemeIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (paused) return;
    timerRef.current = setTimeout(
      () => setThemeIdx((i) => (i + 1) % WALL_THEMES.length),
      SLIDE_MS,
    );
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [themeIdx, paused]);
  const currentTheme = WALL_THEMES[themeIdx];

  return (
    <div className="hy-page lv5">
      <nav className="lv5-nav hy-container">
        <Link to="/" className="lv5-brand">
          <span className="lv5-brand-glyph"></span> SubsWall
        </Link>
        <div className="lv5-nav-right">
          <Link to="/login" className="lv5-nav-signin">Sign in</Link>
          <Link to="/login" className="lv5-btn lv5-btn-primary lv5-btn-sm">Start my wall</Link>
        </div>
      </nav>

      {/* HERO — two-column: copy + sticker, illustration on right */}
      <header className="lv5-hero hy-container">
        <div className="lv5-hero-grid">
          <div className="lv5-hero-copy">
            <h1 className="lv5-h1">
              Get paid by your fans.<br />
              Without becoming a <span className="lv5-h1-em">content factory</span>.
            </h1>
            <p className="lv5-lead">
              SubsWall turns your supporters into a public wall of names on your channel.
              They subscribe once. They stay named. You don't run a newsletter.
            </p>
            <div className="lv5-hero-ctas">
              <Link to="/login" className="lv5-btn lv5-btn-primary lv5-btn-lg">
                Start my wall — it's free
              </Link>
              <Link to="/demo" className="lv5-secondary-link">See a live wall →</Link>
            </div>
            <div className="lv5-trust">
              <strong>2,147</strong> creators · <strong>$184k</strong> paid out · <strong>4.9%</strong> flat fee
            </div>
          </div>

          {/* Wall slideshow — smaller on hero, the showcase of styles */}
          <div
            className="lv5-hero-wall"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            <div className="lv5-wall-frame" key={currentTheme.key}>
              <SubsWall
                tiles={tiles}
                theme={currentTheme.key === "custom" ? "neon" : currentTheme.key}
                aspectRatio={4 / 3}
                bgImageUrl={currentTheme.key === "custom" ? CUSTOM_DEMO_BG : null}
                bgImageOpacity={currentTheme.key === "custom" ? 0.55 : null}
              />
            </div>
            <div className="lv5-slide-controls">
              {WALL_THEMES.map((t, i) => (
                <button
                  key={t.key}
                  type="button"
                  className={`lv5-slide-dot ${i === themeIdx ? "lv5-slide-dot-active" : ""}`}
                  onClick={() => setThemeIdx(i)}
                  aria-label={`Show ${t.label} style`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="lv5-slide-tagline">
              Pick from <strong>{WALL_THEMES.length} wall styles</strong> · {currentTheme.tagline}
            </div>
          </div>
        </div>
      </header>

      {/* TRUSTED BY — friendly social-proof strip */}
      <section className="lv5-trusted hy-container">
        <div className="lv5-trusted-text">Trusted by creators on</div>
        <div className="lv5-trusted-logos">
          <span>YouTube</span>
          <span>·</span>
          <span>Twitch</span>
          <span>·</span>
          <span>Substack</span>
          <span>·</span>
          <span>Spotify</span>
          <span>·</span>
          <span>Apple Podcasts</span>
        </div>
      </section>

      {/* CREATORS GRID — BMAC-style example cards */}
      <section className="lv5-creators hy-container">
        <div className="lv5-section-head">
          <h2 className="lv5-h2">
            Creators making real money,<br /><span className="lv5-h2-em">without the admin.</span>
          </h2>
          <p className="lv5-section-sub">
            Six of the 2,000+ walls live on SubsWall right now.
          </p>
        </div>
        <div className="lv5-creators-grid">
          {EXAMPLE_CREATORS.map((c) => (
            <div className="lv5-creator-card" key={c.handle}>
              <div className="lv5-creator-av">{c.emoji}</div>
              <div className="lv5-creator-body">
                <div className="lv5-creator-name">{c.name}</div>
                <div className="lv5-creator-cat">{c.category}</div>
              </div>
              <div className="lv5-creator-stat">
                <strong>{c.supporters}</strong>
                <span>supporters</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW — three big steps with icons */}
      <section className="lv5-how hy-container">
        <div className="lv5-section-head lv5-section-head-center">
          <h2 className="lv5-h2">
            <span className="lv5-h2-em">Three steps.</span> No newsletter.
          </h2>
        </div>
        <div className="lv5-steps">
          <div className="lv5-step">
            <div className="lv5-step-icon">◆</div>
            <h3>Claim your handle</h3>
            <p>
              Sign in with YouTube, pick a name, connect payouts. Your wall lives at <code>subswall.com/yourname</code> in 30 seconds.
            </p>
          </div>
          <div className="lv5-step">
            <div className="lv5-step-icon">◇</div>
            <h3>Fans subscribe in 30 sec</h3>
            <p>
              One screen: name + message + style + monthly amount. Then they're a permanent tile on your wall.
            </p>
          </div>
          <div className="lv5-step">
            <div className="lv5-step-icon">●</div>
            <h3>The wall does the rest</h3>
            <p>
              Bigger tiers, bigger tiles. Anniversaries earn marks. Cancellations fade publicly over 7 days.
            </p>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — quote cards */}
      <section className="lv5-quotes hy-container">
        <div className="lv5-section-head">
          <h2 className="lv5-h2">Creators on switching.</h2>
        </div>
        <div className="lv5-quotes-grid">
          {TESTIMONIALS.map((q) => (
            <div className="lv5-quote-card" key={q.name}>
              <p className="lv5-quote-text">"{q.quote}"</p>
              <div className="lv5-quote-author">
                <div className="lv5-quote-av">{q.name[0]}</div>
                <div>
                  <div className="lv5-quote-name">{q.name}</div>
                  <div className="lv5-quote-role">{q.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING — single statement + ladder + CTA */}
      <section className="lv5-pricing hy-container">
        <div className="lv5-section-head lv5-section-head-center">
          <span className="lv5-tag">Pricing</span>
          <h2 className="lv5-h2">
            One number. <span className="lv5-h1-em">4.9%</span> of what you earn.
          </h2>
          <p className="lv5-section-sub">
            No monthly minimum. No per-supporter fee. No "Pro plan." Plus standard payment processing on top.
          </p>
        </div>
        <div className="lv5-pricing-card">
          <div className="lv5-ladder">
            <div className="lv5-ladder-row"><span className="lv5-ladder-icon">☕</span><strong>$5</strong><em>A coffee</em></div>
            <div className="lv5-ladder-row"><span className="lv5-ladder-icon">🍕</span><strong>$10</strong><em>A pizza</em></div>
            <div className="lv5-ladder-row"><span className="lv5-ladder-icon">🍣</span><strong>$25</strong><em>Sushi night</em></div>
            <div className="lv5-ladder-row"><span className="lv5-ladder-icon">🍷</span><strong>$50</strong><em>A bottle of wine</em></div>
            <div className="lv5-ladder-row"><span className="lv5-ladder-icon">🍽️</span><strong>$100</strong><em>A tasting menu</em></div>
            <div className="lv5-ladder-row"><span className="lv5-ladder-icon">✈️</span><strong>$250</strong><em>A weekend trip</em></div>
          </div>
          <div className="lv5-pricing-foot">
            Supporters can pick any of these monthly tiers. Their tile size scales with the amount.
          </div>
        </div>
      </section>

      {/* FINAL */}
      <section className="lv5-final">
        <div className="hy-container">
          <h2 className="lv5-final-h">
            Your channel deserves a <span className="lv5-h1-em">monument</span>, not a feed.
          </h2>
          <p className="lv5-final-sub">60 seconds. Pick your handle. Get paid forever.</p>
          <div className="lv5-final-ctas">
            <Link to="/login" className="lv5-btn lv5-btn-primary lv5-btn-lg">Start my wall</Link>
            <Link to="/demo" className="lv5-btn lv5-btn-ghost lv5-btn-lg">See a live wall</Link>
          </div>
        </div>
      </section>

      <footer className="lv5-footer">
        <div className="hy-container lv5-footer-inner">
          <div className="lv5-brand">
            <span className="lv5-brand-glyph"></span> SubsWall
          </div>
          <div className="lv5-footer-meta">
            <span>4.9% take · 95.1% to creators</span>
            <span>·</span>
            <Link to="/terms">Terms</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/contact">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
